import { useAPI } from '@/composables/useAPI';

// Thin client for the v2 2FA withdrawal-verification service
// (core-api `ben/2fa-withdrawal-verification`). Each backend endpoint maps to
// one function here so components don't repeat endpoint strings or the
// `{ data }` envelope handling (useAPI already unwraps `response.data.data`).
//
// The shapes mirror docs/2fa-withdrawal-verification-v2.md:
//   - getCurrentVerification()  -> { requestId, method, purpose, expiresAt } | null
//   - issueCode()               -> { success, issued }  (issued: { otpauthUri } for TOTP, null for email)
//   - verify({ requestId, proof }) -> { success, result }
//   - startEnrollment({ method }) -> { requestId, method, issued, ... }  (door 1)
//
// All of these require auth; the token is attached automatically by useAPI.
export function useTwoFactor() {
  // The user's active 2FA method ('totp' | 'email' | 'passkey'), or null if not
  // enrolled. Read-only — for showing current state in account settings.
  async function getActiveMethod() {
    const { get } = useAPI('auth/method');
    const res = await get();
    return (res && res.method) || null;
  }

  // The user's current open verification request, if any. Its `method` drives
  // the proof UI (email code vs authenticator code). Returns null when there's
  // nothing pending.
  async function getCurrentVerification() {
    const { get } = useAPI('auth/currentVerification');
    return await get();
  }

  // (Re)issue the challenge for the current pending verification — resends the
  // email code, or returns a fresh TOTP otpauth URI / passkey options. For email
  // there's nothing to render (`issued` is null); it just triggers the send.
  async function issueCode() {
    const { request } = useAPI('auth/issueCode');
    return await request({});
  }

  // Submit a proof against a SPECIFIC pending request. requestId is required by
  // the backend — a proof must target the exact request it's meant for.
  async function verify({ requestId, proof }) {
    const { request } = useAPI('auth/verify');
    return await request({ requestId, proof });
  }

  // Begin enrolling/switching to a method (door 1 of the mantrap). For TOTP the
  // response carries `issued.otpauthUri` to render as a QR; for email a code is
  // sent. The returned requestId is door 1; confirming it yields door 2.
  async function startEnrollment({ method }) {
    const { request } = useAPI('auth/enroll/start');
    return await request({ method });
  }

  return {
    getActiveMethod,
    getCurrentVerification,
    issueCode,
    verify,
    startEnrollment,
  };
}
